import { ConfirmDialog } from "@/components/ConfirmDialog";
import { errorMessage } from "@/lib/api";
import { useAdminAthlete } from "@/features/admin/hooks/useAdminData";
import { AthleteComposerDialog } from "./AthleteComposerDialog";

/**
 * Edit from a directory row (#908 quick actions). The composer needs the
 * full record (given / family names), so the detail loads first; until it
 * has, a dialog opens straight away showing the load (or its error) with a
 * Close button, so the click is never silent.
 */
export function AthleteRowEditDialog({
  athlete,
  onClose,
}: {
  athlete: { id: string; display_name: string };
  onClose: () => void;
}) {
  const detailQuery = useAdminAthlete(athlete.id);
  const detail = detailQuery.data;
  if (!detail) {
    return (
      <AthleteRowEditPending
        name={athlete.display_name}
        error={detailQuery.error ? errorMessage(detailQuery.error) : null}
        onClose={onClose}
      />
    );
  }
  return (
    <AthleteComposerDialog
      mode="edit"
      athlete={detail.athlete}
      canSetStatus={detail.can_merge}
      onClose={onClose}
      onSaved={onClose}
    />
  );
}

/** Presentational loading / error state (unit-tested on its own). */
export function AthleteRowEditPending({
  name,
  error,
  onClose,
}: {
  name: string;
  error: string | null;
  onClose: () => void;
}) {
  return (
    <ConfirmDialog
      title="Edit athlete"
      cancelLabel={null}
      confirmLabel="Close"
      initialFocus="confirm"
      confirmDisabled={false}
      onConfirm={onClose}
      onClose={onClose}
      data-testid="admin-athletes-row-edit-pending"
      message={
        error ? (
          <p
            role="alert"
            data-testid="admin-athletes-row-error"
            className="font-mono text-xs text-destructive-text"
          >
            {error}
          </p>
        ) : (
          <p className="text-xs text-ink-muted">Loading {name}…</p>
        )
      }
    />
  );
}
